import { useAppSelector } from 'app/hooks';
import Tag from 'components/common/tag';
import ControlHitArea from 'components/inputs/control-hit-area';
import Quantity from 'components/inputs/quantity';
import ProductPhoto from 'components/products/product-photo';
import { AccessoryProductType } from 'data/types';
import { useEffect, useRef, useState } from 'react';
import { formatUSD } from 'utils/helpers';

type QuantityOptionProps = {
  accessory: AccessoryProductType;
  quantity: number;
  compatibleProducts?: string[];
  onChange?: (value: number) => void;
  size?: 'normal' | 'compact';
  disabled?: boolean;
  recommended?: boolean;
  recommendationType?: 'recommended' | 'alternative' | null;
  simpleMode?: boolean;
  includedWithLineSet?: boolean;
  control?: 'checkbox' | 'radio';
  radioGroupName?: string;
  checkedQuantity?: number;
};

export default function QuantityOption({
  accessory,
  quantity,
  compatibleProducts,
  onChange,
  size = 'normal',
  disabled,
  recommended,
  recommendationType,
  simpleMode,
  includedWithLineSet,
  control = 'checkbox',
  radioGroupName,
  checkedQuantity = 1
}: QuantityOptionProps) {
  const { restockData } = useAppSelector((state) => state.data);
  const [value, setValue] = useState(quantity);
  const valueRef = useRef(value);

  valueRef.current = value;

  const handleChange = (newValue: number) => {
    if (newValue === valueRef.current) {
      return;
    }
    setValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  useEffect(() => {
    setValue(quantity);
  }, [quantity]);

  const appliedQuantity = checkedQuantity > 0 ? checkedQuantity : 1;
  const displayQuantity = value >= 1 ? value : appliedQuantity;
  const isChecked = includedWithLineSet || value >= 1;
  const controlDisabled = disabled || includedWithLineSet;

  return (
    <div className={`flex items-center p-5 ${disabled && 'opacity-50'}`}>
      {simpleMode && control === 'radio' && (
        <ControlHitArea disabled={controlDisabled}>
          <input
            type='radio'
            name={radioGroupName}
            aria-label={accessory.name}
            className='mr-4 h-5 w-5 cursor-pointer border-gray-300 text-blue-600 disabled:cursor-not-allowed'
            checked={isChecked}
            onChange={() => handleChange(appliedQuantity)}
            disabled={controlDisabled}
          />
        </ControlHitArea>
      )}
      {simpleMode && control !== 'radio' && (
        <ControlHitArea disabled={controlDisabled}>
          <input
            type='checkbox'
            aria-label={accessory.name}
            className='mr-4 h-5 w-5 cursor-pointer rounded border-gray-300 text-blue-600 disabled:cursor-not-allowed'
            checked={isChecked}
            onChange={(e) =>
              handleChange(e.target.checked ? appliedQuantity : 0)
            }
            disabled={controlDisabled}
          />
        </ControlHitArea>
      )}
      <div className='flex grow items-center gap-4 pr-4'>
        <ProductPhoto product={accessory} />
        <div className='flex flex-col'>
          <div className='flex flex-wrap items-center gap-x-2'>
            <a
              className='font-semibold text-blue-500 hover:text-blue-600'
              href={`https://alpinehomeair.com${accessory.productUrl}`}
              rel='noreferrer'
              target='_blank'
            >
              {accessory.name}
            </a>
            {includedWithLineSet && (
              <span className='text-sm font-semibold text-blue-500'>
                (Included with Line Set)
              </span>
            )}
            {!includedWithLineSet &&
              (recommended || recommendationType === 'recommended') && (
                <span className='text-sm font-semibold text-green-500'>
                  (recommended)
                </span>
              )}
            {simpleMode && displayQuantity > 1 && (
              <span className='text-sm font-semibold text-gray-500'>
                {`x ${displayQuantity}`}
              </span>
            )}
            {accessory.availableStock < value &&
              (restockData?.[accessory.productId]
                ?.fulfillableStockAfterRestocking ?? 0) >= value && (
                <Tag
                  color='yellow'
                  text={`ETA ${new Date(
                    restockData?.[accessory.productId].restockingDate ?? ''
                  ).toLocaleDateString('en-us', {
                    month: 'numeric',
                    day: 'numeric'
                  })}`}
                />
              )}
            {accessory.availableStock <= 0 && (
              <span className='text-sm font-semibold text-red-500'>
                Out of Stock
              </span>
            )}
          </div>
          <span className='text-sm text-gray-600'>{accessory.description}</span>
          {compatibleProducts && (
            <span className='mt-1 text-xs text-gray-500'>
              Compatible with: {compatibleProducts.join(', ')}
            </span>
          )}
        </div>
      </div>
      {!simpleMode && (
        <>
          <div
            className={`border-x border-gray-200 ${
              size === 'compact' ? 'px-4' : 'px-10'
            }`}
          >
            <Quantity
              value={value}
              onChange={(num) => handleChange(num ?? 0)}
            />
          </div>
          <div
            className={`${
              size === 'compact' ? 'w-[90px]' : 'w-[120px]'
            } shrink-0 grow-0 text-right text-lg font-bold`}
          >
            {formatUSD(accessory.price * Math.max(value, 1), true)}
          </div>
        </>
      )}
    </div>
  );
}
